import { IconBed, IconChartDonut, IconClock, IconMessage, IconProgress, IconStack2, IconSticker2, IconUser, IconUserHeart } from '@tabler/icons-react'
import { immer } from 'zustand/middleware/immer'

import { AstraIcon } from '~/astra'
import { Schemas } from '~/clients/api-client'

import { Slice } from '../store'
import { PageConfigSliceState } from './types'

export const layoutOptions = {
    day: ['table', 'timeline'],
    week: ['calendar', 'board'],
} as const

export type PageKey = keyof typeof layoutOptions
export type DayViewOption = (typeof layoutOptions)['day'][number]
export type WeekViewOption = (typeof layoutOptions)['week'][number]

const isPageKey = (key: string): key is PageKey => Object.keys(layoutOptions).includes(key)
const isViewOption = (option: string): option is DayViewOption | WeekViewOption => [...layoutOptions.day, ...layoutOptions.week].includes(option)

export const visibilityOptions = {
    showPostOps: 'showPostOps',
    showPatients: 'showPatients',
    showSurgeryGroups: 'showSurgeryGroups',
    showDuration: 'showDuration',
    showTimeLeft: 'showTimeLeft',
    showRoomTime: 'showRoomTime',
    showMetrics: 'showMetrics',
    showPractitioners: 'showPractitioners',
    showComments: 'showComments',
    showNotes: 'showNotes',
}

export type VisibilityOptionKeys = keyof typeof visibilityOptions

// State uses visibility options, and selected layouts per page
type State = PageConfigSliceState<VisibilityOptionKeys, PageKey>

const initialState: State = {
    selectedLayouts: {
        day: 'table',
        week: 'calendar',
    },
    visibilityOptions: {
        showPostOps: { value: true, label: 'Postoperativ', icon: <AstraIcon icon={IconBed} />, level: 'page' },
        showPatients: { value: true, label: 'Pasienter', icon: <AstraIcon icon={IconUserHeart} />, level: 'item' },
        showDuration: { value: true, label: 'Varighet', icon: <AstraIcon icon={IconProgress} />, level: 'item' },
        showTimeLeft: { value: true, label: 'Tid igjen', icon: <AstraIcon icon={IconClock} />, level: 'item' },
        showRoomTime: { value: true, label: 'Stuetid', icon: <AstraIcon icon={IconClock} />, level: 'item' },
        showMetrics: { value: true, label: 'KPI Estimater', icon: <AstraIcon icon={IconChartDonut} />, level: 'item' },
        showPractitioners: { value: true, label: 'Kirurger', icon: <AstraIcon icon={IconUser} />, level: 'item' },
        showComments: { value: true, label: 'Kommentar', icon: <AstraIcon icon={IconMessage} />, level: 'item' },
        showNotes: { value: true, label: 'Beskjed om planendring', icon: <AstraIcon icon={IconSticker2} />, level: 'item' },
        showSurgeryGroups: { value: true, label: 'Operasjonskategorier', icon: <AstraIcon icon={IconStack2} />, level: 'item' },
    },
}

type Actions = {
    actions: {
        toggleVisibility: (optionKey: keyof State['visibilityOptions']) => void
        setViewProfileVisibility: (profile: Schemas['ViewOptionProfileOut']['view_profile']) => void
        setSelectedLayout: (page: PageKey, option: string) => void
    }
}

export type OperationalPlannerViewSlice = {
    operationalPlannerViewOptions: State & Actions
}

export const createOperationalPlannerViewSlice: Slice<OperationalPlannerViewSlice> = immer(set => ({
    operationalPlannerViewOptions: {
        ...initialState,
        actions: {
            setSelectedLayout(page, option) {
                set(state => {
                    if (!isPageKey(page) || !isViewOption(option)) return

                    const allowed = layoutOptions[page]
                    if (!allowed.includes(option)) return

                    const selectedLayouts = state.operationalPlannerViewOptions.selectedLayouts
                    if (!selectedLayouts) return
                    selectedLayouts[page] = option
                })
            },
            toggleVisibility: (optionKey: keyof State['visibilityOptions']) =>
                set(state => {
                    const option = state.operationalPlannerViewOptions.visibilityOptions[optionKey]
                    option.value = !option.value
                }),
            setViewProfileVisibility: (viewProfile: Schemas['ViewOptionProfileOut']['view_profile']) =>
                set(state => {
                    const visibilityOptionKeys = Object.keys(visibilityOptions) as VisibilityOptionKeys[]
                    const profileOptions = (viewProfile ?? {}) as Record<VisibilityOptionKeys, boolean>

                    state.operationalPlannerViewOptions.visibilityOptions = visibilityOptionKeys.reduce(
                        (acc, key) => {
                            // Default to true if option is not specified in profile
                            const value = key in profileOptions ? profileOptions[key] : true

                            acc[key] = {
                                ...state.operationalPlannerViewOptions.visibilityOptions[key],
                                value,
                            }
                            return acc
                        },
                        {} as State['visibilityOptions']
                    )
                }),
        },
    },
}))
